import Immutable, { OrderedMap } from 'immutable';
import { useTranslator } from '@bluebeam/react-i18n';
import LoadingAnimation from 'components/LoadingAnimation';
import Navigation from 'components/Navigation';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { PublishRequest } from 'records/PublishRequest';
import { deleteUnpublishedPages, publishPages } from 'services/pageService';
import * as toast from 'utils/toast';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { SelectOption } from 'components/inputs/BaseSelect';
import AuditList from './AuditList';
import AuditSubheader from './AuditSubheader';

interface Params {
  processingStatusId: string;
}

interface Props extends WithAppStoreProps, RouteComponentProps<Params> {}

const AuditPage: React.FC<Props> = ({ appState, actions, match, history }) => {
  const { translate } = useTranslator(['toastText', 'auditPage']);
  const [pagesLoaded, setPagesLoaded] = React.useState(false);
  const {
    selectedProject,
    publishRequests,
    selectedPublishRequests,
    processingStatuses,
    pagesReadyForReview,
    projects,
    settings: { sheets: sheetSettings },
  } = appState;
  const { processingStatusId } = match.params;

  const getToastMessages = () => {
    return {
      fileUploadError: {
        title: translate('toastText:fileUploadError.title'),
        message: translate('toastText:fileUploadError.message'),
      },
      drawingsReadyForReview: {
        title: translate('toastText:drawingsReadyForReview.title'),
        message: translate('toastText:drawingsReadyForReview.message'),
      },
      fileUploadSuccess: {
        title: translate('toastText:fileUploadSuccess.title'),
        message: translate('toastText:fileUploadSuccess.message'),
      },
    };
  };

  const loadPage = async (): Promise<void> => {
    if (!processingStatuses.has(processingStatusId)) {
      const toastText = getToastMessages();
      await actions.getProcessingStatuses(toastText);
    }

    await actions.getSheetSettings();
    await actions.getSheets();
    await actions.getPagesReadyForReview(processingStatusId);
    await actions.clearSelectedPublishRequests();
    await actions.generatePublishRequests();

    setPagesLoaded(true);
  };

  React.useEffect(() => {
    !pagesLoaded && loadPage();

    return () => {
      setPagesLoaded(false);
    };
  }, []);

  const { publishRequestsBySheetNumber, hasUniquePublishRequests } =
    React.useMemo(() => {
      const publishRequestsBySheetNumber = publishRequests.reduce(
        (acc, publishRequest) => {
          return acc.set(
            publishRequest.sheetnumber,
            acc.get(publishRequest.sheetnumber, 0) + 1
          );
        },
        Immutable.OrderedMap<PublishRequest['sheetnumber'], number>()
      );

      return {
        publishRequestsBySheetNumber,
        hasUniquePublishRequests: !publishRequestsBySheetNumber.filter(
          (count) => count > 1
        ).size,
      };
    }, [publishRequests]);

  const handleAuditCancel = (): void => {
    history.push(`/project/${appState.selectedProject}/drawings/uploads`);
  };

  const handlePublish = async (): Promise<void> => {
    let pagesToPublish: PublishRequest[] = [];
    if (selectedPublishRequests.size === 0) {
      pagesToPublish = [...publishRequests.values()];
    } else {
      pagesToPublish = [
        ...publishRequests
          .filter((v) => selectedPublishRequests.contains(v.pageid))
          .values(),
      ];
    }

    await publishPages(selectedProject, pagesToPublish);

    toast.success(
      translate('toastText:drawingsPublished.message'),
      translate('toastText:drawingsPublished.title')
    );

    actions.getMappedSheetVersions();

    history.push(`/project/${selectedProject}/drawings/published`);
  };

  const handleDeletePages = async (): Promise<void> => {
    const requests = new Map<string, string[]>();
    const toastText = getToastMessages();

    pagesReadyForReview
      .filter((p) => selectedPublishRequests.contains(p.pageid))
      .forEach((p) => {
        if (requests.has(p.documentid)) {
          requests.get(p.documentid)?.push(p.pageid);
        } else {
          requests.set(p.documentid, [p.pageid]);
        }
      });
    await Promise.all(
      Array.from(requests.entries()).map((req) =>
        deleteUnpublishedPages(selectedProject, req[0], req[1])
      )
    );
    let emptyRFR = false;

    if (selectedPublishRequests.size === pagesReadyForReview.size) {
      emptyRFR = true;
    }
    actions.clearProcessingStatuses();
    actions.getProcessingStatuses(toastText);
    actions.deleteSelectedPublishReqs();
    if (emptyRFR) {
      history.push(`/project/${appState.selectedProject}/drawings/uploads`);
    }
  };

  const getSelectedProject = () => {
    return projects.get(selectedProject);
  };

  if (!getSelectedProject()) {
    return null;
  }

  return (
    <Navigation>
      <div id="audit-page" className="mx4 mt3">
        {pagesLoaded ? (
          <>
            <AuditSubheader
              onAuditCancel={handleAuditCancel}
              onPublish={handlePublish}
              disablePublish={!hasUniquePublishRequests || !pagesLoaded}
            />

            <AuditList
              publishRequestsBySheetNumber={publishRequestsBySheetNumber}
              deleteSelectedPages={handleDeletePages}
            />
          </>
        ) : (
          <div
            className="flex-center"
            style={{
              height: 'calc(100vh - 148px)',
            }}
          >
            <div className="column items-center">
              <LoadingAnimation
                pageName={translate('auditPage:loadingUploads')}
              />
            </div>
          </div>
        )}
      </div>
    </Navigation>
  );
};

export default withAppStore(AuditPage);
